import React from 'react';
import { Typography, Box, Grid, Dialog, DialogContent, useMediaQuery, Button, Link, Tooltip } from '@mui/material';
import { makeStyles } from '@mui/styles';
import RoundCloseIcon from '../../icons/RoundCloseIcon.svg';
import RoundBackIcon from '../../icons/RoundBackIcon.svg';
import { TieredPricingTable } from '../../pmui/onboarding/pricingCard';
import { PaymentTypeEnum } from '@inosio/payments-sdk';
import BankIcon from '../../icons/Bank_Icon.svg';
import GreyBankIcon from '../../icons/GreyBankIcon.svg';
import { getAvailablePaymentMethods, useIsPmAdhoc } from '../../resui/payments/components/UseSharedMethods';
import { useSelector } from 'react-redux';
import { selectResidentPaymentConfigs, selectResidentResidentFees } from '../../pmui/residents/saga/pmResidentsSlice';
import { selectPaymentConfigs, selectPaymentFeesByResident, selectResidentDetails } from '../../resui/payments/sagas/residentpaymentsSlice';
import AddNewCardPaymentModal from '../../shared/Modal/AddNewCardPaymentModel';
import AddNewBankPaymentModal from '../../shared/Modal/AddNewBankPaymentMethod';
import { UNREG_DEBIT_CARD_GENERIC } from '../../utils/constants';
import DebitCardIcon from '../../icons/DebitCardIcon.svg';
import GreyDebitCardIcon from '../../icons/GreyDebitCardIcon.svg';
import HelpIcon from '@mui/icons-material/Help';

const useStyles = makeStyles(() => ({
  dbFaqText: {
    fontFamily: 'Work Sans',
    fontStyle: 'normal',
    fontWeight: 400,
    fontSize: '10px',
    lineHeight: '22px',
    color: '#39A2DB !important',
    marginTop: '18px',
    cursor: 'pointer',
    marginLeft: '5px',
  },
}));

interface DebitCardNonDiscountPropTypes {
  allFees: any[];
  thisDbFee: number;
  open: boolean;
  onClose: () => void;
  proceedWithFee: () => void;
  back: () => void;
  availablePaymentMethods?: PaymentTypeEnum[];
  targetDate: number;
  irpPaymentClass: string;
  smallVersion?: boolean;
}

function DebitCardNonDiscountModal(props: DebitCardNonDiscountPropTypes) {
  const isOnMobileScreen = useMediaQuery('(max-width:600px)');
  const style = { height: isOnMobileScreen ? '' : '675px' };
  const classes = useStyles();
  const isPmAdhoc = useIsPmAdhoc();
  const paymentConfigs = useSelector(isPmAdhoc ? selectResidentPaymentConfigs : selectPaymentConfigs);
  const paymentFeesByResident = useSelector(isPmAdhoc ? selectResidentResidentFees : selectPaymentFeesByResident);
  const residentsDetails = useSelector(selectResidentDetails);
  const availablePaymentMethods: PaymentTypeEnum[] = props.availablePaymentMethods ?? getAvailablePaymentMethods(props.targetDate, paymentConfigs?.PaymentTypeConfigs, paymentFeesByResident, residentsDetails);

  const [showDBTieredPricing, setShowDBTieredPricing] = React.useState(false);
  const [showACHTieredPricing, setshowACHTieredPricing] = React.useState(false);
  const [openDebitCardModal, setOpenDebitCardModal] = React.useState(false);
  const [openBankModal, setOpenBankModal] = React.useState(false);

  let bankFeesAmount: any = null;
  let debitCardFeesAmount: any = null;
  let creditCardFeesAmount: any = null;
  if (paymentFeesByResident) {
    bankFeesAmount = paymentFeesByResident.length && paymentFeesByResident.find((feeMethod) => feeMethod?.PaymentType === 'ACH' && feeMethod?.PaymentClass === props.irpPaymentClass);
    debitCardFeesAmount = paymentFeesByResident.length && paymentFeesByResident.find((feeMethod) => feeMethod?.PaymentType === 'DB' && feeMethod?.PaymentClass === props.irpPaymentClass);
    creditCardFeesAmount = paymentFeesByResident.length && paymentFeesByResident.find((feeMethod) => feeMethod?.PaymentType === 'CC' && feeMethod?.PaymentClass === props.irpPaymentClass);
  }

  const handleCloseNewPayMethodModal = () => {
    setOpenDebitCardModal(false);
    setOpenBankModal(false);
    props.onClose();
  };

  const isBankFeeCompletelyPmIncurred = !!((bankFeesAmount?.Tiers?.length && bankFeesAmount.Tiers.every((tf: { PaidBy: string }) => tf.PaidBy === 'P')) || (bankFeesAmount && bankFeesAmount.PaidBy === 'P' && !bankFeesAmount.Tiers.length));
  const isDbFeeCompletelyPmIncurred = !!((debitCardFeesAmount?.Tiers?.length && debitCardFeesAmount.Tiers?.every((tf: { PaidBy: string }) => tf.PaidBy === 'P')) || (debitCardFeesAmount && debitCardFeesAmount.PaidBy === 'P' && !debitCardFeesAmount.Tiers.length));
  
  const handleBack = () => {
    props.back();
    props.onClose();
  };

  return (
    <React.Fragment>
      <Dialog
        open={props.open}
        onClose={props.onClose}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
        fullScreen={isOnMobileScreen}
        BackdropProps={{
          invisible: true,
        }}
        fullWidth={true}
        disableEnforceFocus
        style={{
          background: 'none',
          borderRadius: !isOnMobileScreen ? '24px' : undefined,
        }}
        PaperProps={{
          style: {
            pointerEvents: 'auto',
            borderRadius: !isOnMobileScreen ? '24px' : undefined,
          },
        }}
      >
        <DialogContent sx={{ padding: '20px 0px' }}>
          <Box sx={style}>
            <Grid container spacing={2} style={{ padding: '5px 24px 8px' }}>
              <Grid item xs={2}>
                <img src={RoundBackIcon} alt="close" style={{ cursor: 'pointer', marginLeft: isOnMobileScreen ? '30px' : '0px' }} onClick={handleBack} />
              </Grid>
              <Grid item xs={8}>
                <Typography id="payment-method-title" style={{ textAlign: 'center' }}>
                  Heads Up!
                </Typography>
              </Grid>
              <Grid item xs={2}>
                <img src={RoundCloseIcon} alt="close" style={{ cursor: 'pointer', marginLeft: isOnMobileScreen ? '0px' : '50px' }} onClick={props.onClose} />
              </Grid>
              <Grid item xs={12}>
                <Typography
                  style={{
                    fontStyle: 'normal',
                    fontSize: '16px',
                    lineHeight: '22px',
                    padding: '20px',
                    fontFamily: 'Open Sans',
                    fontWeight: 700,
                    color: ' #14142B',
                  }}
                >
                  This debit card does not qualify for the discounted debit card pricing option.
                </Typography>
                <Typography
                  style={{
                    fontFamily: 'Work Sans',
                    fontStyle: 'normal',
                    fontWeight: 400,
                    fontSize: '16px',
                    lineHeight: '18px',
                    color: ' #14142B',
                    marginTop: '15px',
                  }}
                >
                  The transaction fee for this card will be <b>${(props.thisDbFee / 100).toFixed(2)}</b>.<br />
                  <br />
                  There are more cost effective options:
                </Typography>
              </Grid>
              <Grid container item textAlign={'center'} xs={12} sx={{ display: 'flex !important', justifyContent: 'space-between', alignItems: 'center' }}>
                <Grid item xs={6}>
                  <Box
                    sx={{
                      width: '240px',
                      height: 'max-content',
                      position: 'absolute',
                      top: '90px',
                      right: '0',
                      zIndex: '9',
                      background: '#ffffff',
                      border: '2px solid #eeeeee',
                      borderRadius: '5px',
                      display: showACHTieredPricing ? undefined : 'none',
                    }}
                  >
                    {!!bankFeesAmount?.Tiers && <TieredPricingTable tiers={bankFeesAmount.Tiers} />}
                  </Box>
                  {openBankModal && <AddNewBankPaymentModal handleCloseBankModal={handleCloseNewPayMethodModal} openBankModal={openBankModal} handleCloseGoBackBankModal={handleCloseNewPayMethodModal} smallVersion={props.smallVersion} />}
                  <Typography id="dbRateAchTryAch" style={{ fontSize: '13px', height:'39px' }}>
                    Use account and routing number
                  </Typography>
                  <div>
                    <img src={availablePaymentMethods.includes(PaymentTypeEnum.Ach) ? BankIcon : GreyBankIcon} alt="bank" />
                  </div>
                  <Typography id="dbRateAchTryAchFree" style={{ fontSize: '13px' }}>
                    {isBankFeeCompletelyPmIncurred ? (
                      <>Free</>
                    ) : props.irpPaymentClass === 'VAP' && bankFeesAmount?.Tiers?.length ? (
                      <Link
                        onMouseEnter={(event: React.SyntheticEvent) => {
                          setshowACHTieredPricing(true);
                        }}
                        onMouseLeave={(event: React.SyntheticEvent) => {
                          setshowACHTieredPricing(false);
                        }}
                      >
                        View fees
                      </Link>
                    ) : (
                      `Fees: ${bankFeesAmount && (bankFeesAmount.FeeAmount / 100).toFixed(2)}`
                    )}
                  </Typography>
                  <div>
                    <Button disabled={!availablePaymentMethods.includes(PaymentTypeEnum.Ach)} style={{ marginBottom: '8px' }} variant="contained" id="add-bank-modal-btn" onClick={() => setOpenBankModal(true)}>
                      Bank Account
                    </Button>
                  </div>
                  {!availablePaymentMethods.includes(PaymentTypeEnum.Ach) && (
                    <Typography id="dbRateAchNotAccepted" style={{ fontSize: '13px' }}>
                      ACH not accepted
                    </Typography>
                  )}
                </Grid>
                <Grid item xs={6}>
                  <Box
                    sx={{
                      width: '240px',
                      height: 'max-content',
                      position: 'absolute',
                      top: '90px',
                      left: '0',
                      zIndex: '9',
                      background: '#ffffff',
                      border: '2px solid #eeeeee',
                      borderRadius: '5px',
                      display: showDBTieredPricing ? undefined : 'none',
                    }}
                  >
                    {!!debitCardFeesAmount?.Tiers && <TieredPricingTable tiers={debitCardFeesAmount.Tiers} />}
                  </Box>
                  {openDebitCardModal && <AddNewCardPaymentModal handleCloseCardModal={handleCloseNewPayMethodModal} openCardModal={openDebitCardModal} handleCloseGoBackCardModal={handleCloseNewPayMethodModal} smallVersion={props.smallVersion} title={'Debit'} />}
                  <Typography id="dbRateAchTryCard" style={{ fontSize: '13px', height:'39px' }}>
                    Try another card
                  </Typography>
                  <div>
                    <img src={availablePaymentMethods.includes(PaymentTypeEnum.Db) ? DebitCardIcon : GreyDebitCardIcon} alt="card" />
                  </div>
                  <Typography id="dbRate1" style={{ fontSize: '13px' }}>
                    {isDbFeeCompletelyPmIncurred ? (
                      <>Free</>
                    ) : props.irpPaymentClass === 'VAP' && debitCardFeesAmount?.Tiers?.length ? (
                      <Link
                        onMouseEnter={(event: React.SyntheticEvent) => {
                          setShowDBTieredPricing(true);
                        }}
                        onMouseLeave={(event: React.SyntheticEvent) => {
                          setShowDBTieredPricing(false);
                        }}
                      >
                        View fees
                        <Tooltip leaveTouchDelay={5000} enterTouchDelay={0} title={UNREG_DEBIT_CARD_GENERIC} placement="bottom">
                          <HelpIcon sx={{ fontSize: 17, color: '#C4C4C4', cursor: 'pointer' }} />
                        </Tooltip>
                      </Link>
                    ) : (
                      <>
                        Fees: ${debitCardFeesAmount && (debitCardFeesAmount.FeeAmount / 100).toFixed(2)} - ${creditCardFeesAmount && (creditCardFeesAmount.FeeAmount / 100).toFixed(2)}
                        <Tooltip leaveTouchDelay={5000} enterTouchDelay={0} title={UNREG_DEBIT_CARD_GENERIC} placement="bottom">
                          <HelpIcon sx={{ fontSize: 17, color: '#C4C4C4', cursor: 'pointer' }} />
                        </Tooltip>
                      </>
                    )}
                  </Typography>
                  <div>
                    <Button disabled={!availablePaymentMethods.includes(PaymentTypeEnum.Db)} style={{ marginBottom: '8px' }} variant="contained" id="add-card-btn" onClick={() => setOpenDebitCardModal(true)}>
                      Debit Card
                    </Button>
                  </div>
                  {!availablePaymentMethods.includes(PaymentTypeEnum.Db) && (
                    <Typography id="dbRateDbNotAccepted" style={{ fontSize: '13px' }}>
                      Debit card not accepted
                    </Typography>
                  )}
                </Grid>
              </Grid>
            </Grid>
            <Grid container spacing={2} textAlign="center">
              <Grid item xs={12} md={6}>
                {/* link here */}
                <Box
                  sx={{
                    margin: '29px 16px 0px',
                    verticalAlign: 'text-bottom',
                  }}
                >
                  <Typography
                  >
                    <span className={classes.dbFaqText} onClick={() => window.open('http://support.inosio.com/article/debit-card-processing')}>
                      Learn more about card processing.
                    </span>
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} md={6}>
                <Box
                  sx={{
                    margin: '29px 16px 0px',
                    verticalAlign: 'text-bottom',
                  }}
                >
                  <Button
                  variant="outlined" 
                    style={{
                      background: '#fff',
                      borderRadius: '24px',
                      border: "2px !important solid #39A2DB !important",
                      width: '230px',
                      height: '36px',
                      color: '#39A2DB',
                      fontFamily: 'Open Sans',
                      fontStyle: 'normal',
                      fontWeight: 700,
                      fontSize: '14px',
                      lineHeight: '18px',
                      textTransform: 'capitalize',
                      padding: '10px',
                    }}
                    onClick={() => {
                      props.onClose();
                      props.proceedWithFee();
                    }}
                    id="proceedWithDebitNoDiscountFeeBtn"
                    className={'active2'} //it's for e2e test
                  >
                    Proceed with ${(props.thisDbFee / 100).toFixed(2)} fee
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
      </Dialog>
    </React.Fragment>
  );
}
export default DebitCardNonDiscountModal;
